import { BILLING_TAB_SEGMENTS, paths, type BillingTabKey } from '@/app/router/paths';
import type { IconName } from '@/design-system';

import { PLAN_CHOICE_ANCHOR } from './components/SubscriptionCard';
import { BUY_INSPECTIONS_ANCHOR } from './components/PurchaseCard';
import { billingTabsCopy } from './copy';

/**
 * De tabbladen van Facturatie, als gegevens.
 *
 * Klanten zagen op één pagina niet meer wat van wie was: hun abonnement bij
 * Smartkeur stond tussen de facturen aan hun eigen klanten. Daarom twee paar
 * tabbladen, in deze volgorde:
 *
 * - **Abonnement & tegoed** - wat het bedrijf bij Smartkeur afneemt: het
 *   abonnement, het tegoed en keuringen kopen. Op `/facturatie` zelf, waar de
 *   ankers en de terugkeer van de betaalpagina heen wijzen.
 * - **Facturen van Smartkeur** - wat het bedrijf aan Smartkeur betaalde, en het
 *   grootboek van het tegoed.
 * - **Facturen aan klanten** - de facturen die het bedrijf zijn klanten stuurt,
 *   met de omzet van de maand.
 * - **Tarieven** - wat het bedrijf zijn klanten per dienst rekent.
 *
 * De eerste twee zijn alleen voor de eigenaar (net als voorheen de kaarten van
 * het abonnement en de betalingen). Wat je niet mag zien bestaat niet: geen grijs
 * tabblad, en het adres ervan leidt naar het eerste tabblad dat je wél ziet.
 */
export interface BillingTab {
    key: BillingTabKey;
    /** Het woord in de kop, en de kop boven de kaarten. */
    label: string;
    to: string;
    /** Het lijn-icoon voor het label. */
    icon: IconName;
}

const ORDER: readonly BillingTabKey[] = ['plan', 'platformInvoices', 'invoices', 'tariffs'];

/** Wat je bij Smartkeur afneemt, wat je ontvangt, wat je verstuurt, wat je rekent. */
const ICONS: Record<BillingTabKey, IconName> = {
    plan: 'planning',
    platformInvoices: 'download',
    invoices: 'send',
    tariffs: 'billing',
};

const OWNER_ONLY: ReadonlySet<BillingTabKey> = new Set(['plan', 'platformInvoices']);

/** De tabbladen die deze persoon ziet, in de volgorde van de kop. */
export function billingTabsFor(isOwner: boolean): BillingTab[] {
    return ORDER.filter((key) => isOwner || !OWNER_ONLY.has(key)).map((key) => ({
        key,
        label: billingTabsCopy[key].label,
        to: paths.billingTab(key),
        icon: ICONS[key],
    }));
}

/**
 * Het tabblad op dit adres: Abonnement & tegoed op `/facturatie`, de facturen aan
 * klanten op `/facturatie/facturen`. `null` bij een onbekend of dieper pad en bij
 * een tabblad dat je niet mag zien. Hoofdletters en een slash aan het eind tellen
 * niet, net als in de router; de pagina zet het adres daarna recht.
 */
export function billingTabAt(tabs: readonly BillingTab[], pathname: string): BillingTab | null {
    const path = pathname.toLowerCase().replace(/\/+$/, '');

    if (path !== paths.billing && !path.startsWith(`${paths.billing}/`)) {
        return null;
    }

    const segment = path.slice(paths.billing.length + 1);

    return tabs.find((tab) => BILLING_TAB_SEGMENTS[tab.key] === segment) ?? null;
}

/**
 * Plekken waar een link heen kan, met het tabblad waarop ze staan. Het dashboard,
 * een 402 en de mails linken naar `#abonnement` en `#keuringen-kopen`.
 */
const ANCHORS: Readonly<Record<string, BillingTabKey>> = {
    [PLAN_CHOICE_ANCHOR]: 'plan',
    [BUY_INSPECTIONS_ANCHOR]: 'plan',
};

/** Het tabblad waarop de plek uit `#…` staat, als je dat tabblad ziet; anders `null`. */
export function billingTabForHash(tabs: readonly BillingTab[], hash: string): BillingTab | null {
    const key = ANCHORS[anchorId(hash)];

    return key === undefined ? null : (tabs.find((tab) => tab.key === key) ?? null);
}

/** `#keuringen-kopen` → `keuringen-kopen`; een kapotte codering blijft zoals hij is. */
function anchorId(hash: string): string {
    const id = hash.startsWith('#') ? hash.slice(1) : hash;

    try {
        return decodeURIComponent(id);
    } catch {
        return id;
    }
}
